import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useGetCaseList } from 'src/shared/queries/useGetCaseList';
import { useGetUser } from 'src/shared/queries/useGetUser';
import NoCasesIcon from '@icons/no-documents.svg';
import NoCasesDeloitteIcon from '@icons/deloitte/no-cases.svg';
import { Tag } from '../../../design-system/elements/Tag/Tag';
import { TagVariant } from '../../../design-system/elements/Tag/Tag.types';
import { Skeleton } from '@ds/elements/Skeleton/Skeleton';
import { ICaseAssociationModel } from 'src/shared/types/Case';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { isValid, parseISO } from 'date-fns';
import { Country } from 'src/shared/types/Common';
import { FallbackPlaceholderText } from 'src/utils/constants/common';
import { CaseDocumentsTabs } from 'src/utils/constants/Case';
import { Tooltip } from '@ds';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { useThemeStore } from 'src/stores/ThemeStore';
import { useImmigrationInstanceStore } from 'src/stores/ImmigrationInstanceStore';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import { redirectToRemoteCaseDetails, shouldSkipGlobalCaseTransitionModal } from 'src/utils/services/case';
import GlobalCaseTransitionModal, {
  GlobalCaseTransitionModalType,
} from 'src/components/GlobalCaseTransitionModal/GlobalCaseTransitionModal';
import styles from './Cases.module.scss';
import { Toast } from '@ds';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { AidiProgressStatus, useAidiAccess } from 'src/utils/hooks/useAidiAccess';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';

const PlaceholderItems = Array.from({ length: 5 }, (_, i) => ({ id: i }));

const Cases: React.FC = () => {
  const { data: user } = useGetUser();
  const flags = useFlags();
  const hasHiddenAttorneyData = !!flags[FeatureFlags.HideLegalContacts];
  const { data: casesRaw, isLoading: loadingCases } = useGetCaseList(user?.beneId, true);
  const cases =
    casesRaw?.flatMap((caseItem: ICaseAssociationModel) =>
      caseItem.subProjects ? [caseItem, ...caseItem.subProjects] : [caseItem]
    ) ?? [];
  const { data: countries } = useGetCountries();
  const { navigate } = useNavigateWithSecureParams();
  const { isDeloitteTheme } = useThemeStore();
  const { immigrationInstance } = useImmigrationInstanceStore();
  const [isLongPress, setIsLongPress] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ICaseAssociationModel | null>(null);
  const toastRef = useRef<IToastr>(null);

  // Track a pending navigation request so we can evaluate full AIDI access BEFORE deciding destination.
  const [pendingCaseId, setPendingCaseId] = useState<number | null>(null);
  const [pendingIsActive, setPendingIsActive] = useState<boolean | undefined>(undefined);
  // Evaluation phases:
  // idle -> no row clicked yet
  // flag-check -> using flagOnly mode to quickly determine if flag is enabled
  // full -> full eligibility (questionnaire + stage)
  const [evaluationPhase, setEvaluationPhase] = useState<'idle' | 'flag-check' | 'full'>('idle');

  // Always invoke hook (will only meaningfully resolve when pendingCaseId is set). Full eligibility: flag + questionnaire + stage.
  const {
    shouldDisableLegacy: hideLegacyQuestionaireOrDocuments,
    loading: loadingAidiAccess,
    reasons,
    accessGranted,
    aidiProgressStatus,
  } = useAidiAccess({
    caseId: pendingCaseId,
    flagOnly: evaluationPhase === 'flag-check',
    // Required for aidiProgressStatus to be derived at all; without it the hook always
    // reports IDLE and every case would look like a first-time entry.
    aidiStatus: true,
  });
  const useUrlCaseId = useMemo(() => flags['fnx-show-backtodocuments'] ?? false, [flags]);
  const handleRowClick = (event: any) => {
    if (!event.data) return;
    if (!isLongPress) {
      const remoteCaseId = event.data?.remoteCaseId || 0;
      const sourceInstance = event.data?.sourceInstance;
      // A case is remote if it has a remoteCaseId OR if sourceInstance doesn't match the current instance
      const isRemoteCase =
        remoteCaseId > 0 ||
        (sourceInstance && immigrationInstance !== undefined && sourceInstance !== immigrationInstance);
      if (isRemoteCase) {
        const universalCaseId = event.data?.universalCaseId || '';
        shouldSkipGlobalCaseTransitionModal()
          ? redirectToRemoteCaseDetails(universalCaseId)
          : setSelectedRow(event.data);
      } else {
        const clickedCaseId = event.data?.caseId;
        setPendingCaseId(clickedCaseId);
        setPendingIsActive(event.data?.isActive);
        setEvaluationPhase('flag-check');
        // Show toast immediately so user gets instant feedback instead of waiting for flag check round-trip
        toastRef.current?.clear();
        toastRef.current?.show({ message: 'Loading...', severity: 'info', sticky: true });
      }
    }
    setIsLongPress(false);
  };

  // Transition from flag-check to either legacy (flag false) or full (flag true)
  useEffect(() => {
    if (evaluationPhase !== 'flag-check' || !pendingCaseId) return;
    if (loadingAidiAccess) return;
    const flagFalse = reasons.includes('feature_flag_false');
    if (flagFalse) {
      // Navigate legacy immediately
      const tab = pendingIsActive ? undefined : CaseDocumentsTabs.Timeline;
      const url = `/case-documents?${tab ? `tab=${tab}` : ''}`;
      navigate(url, { secureParams: { caseId: pendingCaseId, tab } });
      setPendingCaseId(null);
      setPendingIsActive(undefined);
      setEvaluationPhase('idle');
    } else {
      // Flag true -> proceed to full evaluation (toast already shown on row click)
      setEvaluationPhase('full');
    }
  }, [evaluationPhase, pendingCaseId, pendingIsActive, loadingAidiAccess, reasons, navigate]);

  // When full AIDI access evaluation completes, decide navigation target.
  useEffect(() => {
    if (evaluationPhase !== 'full' || !pendingCaseId) return;
    if (loadingAidiAccess) return;

    // Full evaluation done
    if (hideLegacyQuestionaireOrDocuments || accessGranted) {
      toastRef.current?.clear();
      // First entry into AIDI (no agent record yet) is routed through the splash screen;
      // returning users go straight to documents. Mirrors getQAidiPath in CaseStage.
      const isFirstAidiEntry = aidiProgressStatus === AidiProgressStatus.IDLE;
      if (useUrlCaseId) {
        const path = isFirstAidiEntry ? `/aidi/case/${pendingCaseId}/welcome` : `/aidi/case/${pendingCaseId}`;
        navigate(path, { secureParams: { caseId: pendingCaseId } });
      } else {
        const path = isFirstAidiEntry ? '/aidi/case/welcome' : '/aidi/case';
        navigate(path, { secureParams: { caseId: pendingCaseId } });
      }
    } else {
      const missingQuestionnaire = reasons.includes('missing_target_questionnaire');
      toastRef.current?.clear();
      if (missingQuestionnaire) {
        toastRef.current?.show({ message: 'No questionnaires assigned.', severity: 'info' });
      }
      const tab = pendingIsActive ? undefined : CaseDocumentsTabs.Timeline;
      const url = `/case-documents?${tab ? `tab=${tab}` : ''}`;
      navigate(url, { secureParams: { caseId: pendingCaseId, tab } });
    }

    setPendingCaseId(null);
    setPendingIsActive(undefined);
    setEvaluationPhase('idle');
  }, [
    evaluationPhase,
    pendingCaseId,
    pendingIsActive,
    loadingAidiAccess,
    hideLegacyQuestionaireOrDocuments,
    accessGranted,
    reasons,
    navigate,
    aidiProgressStatus,
    useUrlCaseId,
  ]);

  const renderTooltipCell = (id: number, text: string, columnClass: string) => {
    const uniqueTarget = `tooltip-${id}-${columnClass}`;
    return (
      <>
        <p
          id={uniqueTarget}
          className={styles[columnClass]}
          onClick={handleRowClick}
          onContextMenu={(event) => event.preventDefault()}
        >
          {text}
        </p>
        <Tooltip position="top" target={`#${uniqueTarget}`} content={text} />
      </>
    );
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return FallbackPlaceholderText.NotAvailable;
    const parsedDate = parseISO(dateString);
    return isValid(parsedDate)
      ? getFormattedDate(dateString, UserDateFormat.get())
      : FallbackPlaceholderText.NotAvailable;
  };

  const destinationCountryTemplate = (rowData: ICaseAssociationModel) => {
    const destinationCountry = useMemo(() => {
      return (
        countries?.find((country: Country) => country.countryCode === rowData?.destinationCountry)?.countryName ||
        rowData?.destinationCountry
      );
    }, [rowData?.destinationCountry, countries]);
    return renderTooltipCell(
      rowData.caseId,
      rowData.parentCaseId ? '' : destinationCountry || '',
      'destination-country-column'
    );
  };

  const balNumberTemplate = (rowData: ICaseAssociationModel) => {
    let caseDetails: string = rowData.balNumber ?? '';
    if (rowData.caseType) {
      const subTypesStr = rowData.caseSubTypes.join(' - ');
      const concatStr = (subTypesStr && rowData.caseType + ' - ' + subTypesStr) || rowData.caseType;
      caseDetails = concatStr + ' ' + rowData.balNumber;
    }
    return renderTooltipCell(rowData.caseId, caseDetails, 'bal-number-column');
  };

  const createdDateTemplate = (rowData: ICaseAssociationModel) => {
    return <div className={`${styles['created-date-column']}`}>{formatDate(rowData.createdDateOnly)}</div>;
  };

  const filedDateTemplate = (rowData: ICaseAssociationModel) => {
    return <div className={`${styles['filed-date-column']}`}>{formatDate(rowData.filedDate)}</div>;
  };

  const decisionDateTemplate = (rowData: ICaseAssociationModel) => {
    return (
      <div className={`${styles['decision-date-column']}`}>
        <span>{formatDate(rowData.decisionDate)}</span>
        {rowData.isDenied && (
          <Tag className={`${styles['status-column']}`} variant={TagVariant.Error}>
            Denied
          </Tag>
        )}
      </div>
    );
  };

  const closedDateTemplate = (rowData: ICaseAssociationModel) => {
    return <div className={`${styles['closed-date-column']}`}>{formatDate(rowData.closedDateOnly)}</div>;
  };

  const attorneyTemplate = (rowData: ICaseAssociationModel) =>
    renderTooltipCell(
      rowData.caseId,
      rowData.primaryAttorneyName || FallbackPlaceholderText.NotAvailable,
      'attorney-column'
    );

  const assistantTemplate = (rowData: ICaseAssociationModel) =>
    renderTooltipCell(
      rowData.caseId,
      rowData.primaryAssistantName || FallbackPlaceholderText.NotAvailable,
      'assistant-column'
    );

  const statusTemplate = (rowData: any) => {
    return (
      <Tag className={`${styles['status-column']}`} variant={rowData.isActive ? TagVariant.Success : TagVariant.Info}>
        {rowData.isActive ? 'Active' : 'Closed'}
      </Tag>
    );
  };

  return (
    <div className={`${styles['cases-page']}`}>
      <h2>Cases</h2>
      <div className={`${styles['cases-page__content']}`}>
        <div className={`${styles['cases-page-info']}`}>
          <span className={`${styles['info-icon']}`}></span>
          <span className={`${styles['info-text']}`}>Tap and hold on table cells to reveal full text</span>
        </div>
        {loadingCases ? (
          <DataTable value={PlaceholderItems || []} className={styles['skeleton-table']}>
            <Column header="Destination Country / Location" body={<Skeleton height="35px" width="150px" />} />
            <Column header="Case Details" body={<Skeleton height="35px" width="150px" />} />
            <Column header="Opened Date" body={<Skeleton height="35px" width="98px" />} />
            <Column header="Filed Date" body={<Skeleton height="35px" width="98px" />} />
            <Column header="Decision" body={<Skeleton height="35px" width="98px" />} />
            <Column header="Closed Date" body={<Skeleton height="35px" width="98px" />} />
            {!hasHiddenAttorneyData && <Column header="Attorney" body={<Skeleton height="35px" width="98px" />} />}
            {!hasHiddenAttorneyData && <Column header="Assistant" body={<Skeleton height="35px" width="98px" />} />}
            <Column header="Status" body={<Skeleton height="35px" width="78px" />} />
          </DataTable>
        ) : !loadingCases && (!casesRaw || (cases?.length ?? 0) === 0) ? (
          <div className={`${styles['no-cases']}`}>
            <span className={`${styles['empty-message']}`}>You currently have no active cases.</span>
            <img src={isDeloitteTheme ? NoCasesDeloitteIcon : NoCasesIcon} alt="info icon" />
          </div>
        ) : (
          <DataTable
            value={cases}
            className={styles['p-datatable-cases']}
            rowClassName={(rowData) => (rowData.parentCaseId ? styles['subproject-row'] : styles[''])}
            onRowClick={handleRowClick}
            selectionMode="single"
          >
            <Column
              field="destinationCountry"
              header="Destination Country / Location"
              body={destinationCountryTemplate}
            />
            <Column field="balNumber" header="Case Details" body={balNumberTemplate} />
            <Column field="createdDateOnly" header="Opened Date" body={createdDateTemplate} />
            <Column field="filedDate" header="Filed Date" body={filedDateTemplate} />
            <Column field="decisionDate" header="Decision" body={decisionDateTemplate} />
            <Column field="closedDateOnly" header="Closed Date" body={closedDateTemplate} />
            {!hasHiddenAttorneyData && (
              <Column field="primaryAttorneyName" header="Attorney" body={attorneyTemplate} />
            )}
            {!hasHiddenAttorneyData && (
              <Column field="primaryAssistantName" header="Assistant" body={assistantTemplate} />
            )}
            <Column field="isActive" header="Status" body={statusTemplate} />
          </DataTable>
        )}
      </div>
      {selectedRow && (
        <GlobalCaseTransitionModal
          isModalOpen={true}
          modalType={GlobalCaseTransitionModalType.CASE}
          name={selectedRow.caseName || ''}
          onNavigate={() => redirectToRemoteCaseDetails(selectedRow.universalCaseId || '')}
          onModalClose={() => setSelectedRow(null)}
        />
      )}
      <Toast ref={toastRef} />
    </div>
  );
};

export default Cases;
